/**
 * Presentational "Exclude this case" form. Owns FORM state only — the container (`ExcludeAction`) owns
 * open/close, the `excludeCase` call, the submit state machine, the `loggedAs` snapshot and the gate.
 */
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  Textarea,
  useCheckbox,
  useCheckboxGroup
} from '@chakra-ui/react';
import type { UseCheckboxProps } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import type {
  ExcludeDictionaryEntry,
  ExcludePayload,
  ExcludeReasonKey
} from '../../interface/exclude';
import {
  buildExcludePayload,
  EXCLUDE_REASON_ORDER,
  type ExcludeFormValues,
  type ExcludeValidationError
} from '../../slots/buildExcludePayload';
import { formatLoggedAtParts } from '../../slots/formatLoggedAt';
import {
  EXCLUDE_COLORS,
  EXCLUDE_GLYPH_PX,
  EXCLUDE_SIZES,
  CASE_ACTION_RADIUS
} from '../../styles/tokens';
import { RoundedButton } from '@codametrix/ui-components';
import { ExcludeGlyph, WarningTriangleGlyph } from './ExcludeGlyph';

/**
 * The design-system button atom, re-typed with EXACTLY the extra props used here and no more.
 * `RoundedButton` maps a spectre `className` to a theme variant and spreads everything else onto the
 * underlying Chakra `Button` at runtime, but its own signature is only `ButtonHTMLAttributes`. Typing this
 * as `React.FC<ButtonProps>` would be worse, not better: it would advertise Chakra's entire style-prop
 * surface including `variant`, which `RoundedButton` overwrites from `className`.
 */
const AtomButton = RoundedButton as React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    isLoading?: boolean;
    bg?: string;
    color?: string;
    _hover?: { bg?: string };
  }
>;

export type ExcludeSubmitState = 'idle' | 'excluding' | 'error';

export interface ExcludeModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly loggedAs: {
    readonly name: string;
    readonly at: Date;
    /** IANA zone -- pins the footer timestamp for deterministic stories/tests. */
    readonly timeZone?: string;
  };
  readonly onSubmit: (payload: ExcludePayload) => Promise<void>;
  readonly submitState: ExcludeSubmitState;
  readonly errorMessage?: string;
  /** Configured exclusion-reason vocabulary revealed by "Other" (AC5). Empty/absent -> inline hint. */
  readonly reasonOptions?: readonly ExcludeDictionaryEntry[];
  /** Chakra restores focus here on close (the container passes its trigger ref). */
  readonly finalFocusRef?: React.RefObject<HTMLElement>;
}

/** Dialog title -- the SAME constant names the visible `<h2>`, the dialog's `aria-label`, and (imported by
 *  `ExcludeAction`) the trigger button, so the three cannot drift. */
export const EXCLUDE_MODAL_TITLE = 'Exclude this case';

/** Description text. Deliberately says nothing about archiving: the mock's "…and archives it" asserts the
 *  CASE is archived, and a backend audit established there is no case-archive capability at all. */
const EXCLUDE_MODAL_SUBTITLE =
  'Excluding this case removes it from your active workflow.';

const WARNING_ID = 'exclude-warning';

/** Reason labels. Sentence case per the mock (casing is presentation). The ORDER lives in
 *  `EXCLUDE_REASON_ORDER` so this record cannot drift from what the normalizer emits -- a test asserts every
 *  key has a label. */
const REASON_LABELS: Record<ExcludeReasonKey, string> = {
  excludedProvider: 'Excluded provider',
  excludedSpecialty: 'Excluded specialty',
  excludedFacility: 'Excluded facility',
  duplicate: 'Duplicate',
  other: 'Other'
};

/** Shared eyebrow-label style, mirroring HoldModal's. Uppercase is written literally into the strings so it
 *  cannot cascade into the child "required"/"optional" hints, which stay normal case. */
const EYEBROW = {
  textStyle: 'Subtitle 1',
  color: EXCLUDE_COLORS.inkSoft
} as const;

interface CheckboxPillProps extends UseCheckboxProps {
  readonly label: string;
  readonly testId: string;
  /** Forwarded to the NATIVE input so the dialog's `initialFocusRef` can focus the real checkbox. A ref on
   *  the label or the fieldset would move focus somewhere that is not the control. */
  readonly inputRef?: React.Ref<HTMLInputElement>;
  /**
   * ARIA attributes passed as `getInputProps`' FIRST argument so they land on the native input.
   * Verified the hard way: handing them to `useCheckbox(props)` silently drops them — that hook builds its
   * own attribute set and does not forward arbitrary `aria-*`. Declaring them as explicit props makes the
   * wiring visible instead of looking like it works.
   */
  readonly ariaRequired?: boolean;
  readonly ariaExpanded?: boolean;
  readonly ariaControls?: string;
}

/**
 * One multi-select pill. `useCheckbox` must run inside its own component (rules of hooks forbid calling it
 * per-iteration in a `.map()`), so this renders the real, visually-hidden native `<input type="checkbox">`
 * plus a styled `<span>`.
 *
 * Uses `getLabelProps()`, NOT `getCheckboxProps()`: the latter sets `aria-hidden` (correct for Chakra's own
 * `Checkbox`, where the glyph and the text are separate siblings), but this pill fuses indicator and label
 * into one capsule, so hiding it would leave the input with no accessible name at all.
 *
 * The checked state is NOT colour-only: a leading checkmark appears alongside the fill/border/text change.
 * There is no `forced-colors` handling anywhere in dashboard-ui, so without the mark the checked state would
 * vanish entirely in Windows High Contrast Mode -- on a REQUIRED multi-select, that would leave a low-vision
 * coder unable to tell which reasons they had picked.
 */
const CheckboxPill: React.FC<CheckboxPillProps> = ({
  label,
  testId,
  inputRef,
  ariaRequired,
  ariaExpanded,
  ariaControls,
  ...checkboxProps
}) => {
  const { getInputProps, getLabelProps, state } = useCheckbox(checkboxProps);
  return (
    <Box
      as="label"
      data-testid={testId}
      display="inline-flex"
      cursor={state.isDisabled ? 'not-allowed' : 'pointer'}
    >
      {/* The extra attributes and the ref BOTH go through `getInputProps`. Spreading them after it and
          adding a bare `ref=` would win the JSX merge and clobber Chakra's own merged input ref -- including
          on every pill that passes `inputRef={undefined}`, which would null out its internal ref. Chakra
          spreads its `props` argument FIRST and sets none of these three attributes itself, so they
          survive; the second argument is the ref it merges rather than replaces. */}
      <input
        {...getInputProps(
          {
            'aria-required': ariaRequired || undefined,
            'aria-expanded': ariaExpanded,
            'aria-controls': ariaControls
          },
          inputRef
        )}
      />
      <Box
        as="span"
        {...getLabelProps()}
        textStyle="Body 1"
        display="inline-flex"
        alignItems="center"
        gap="0.375rem"
        px="0.8125rem"
        py="0.4375rem"
        borderRadius="full"
        border="1px solid"
        borderColor={
          state.isChecked ? EXCLUDE_COLORS.chipSelected : EXCLUDE_COLORS.border
        }
        bg={state.isChecked ? EXCLUDE_COLORS.chipSelected : EXCLUDE_COLORS.onFilled}
        color={state.isChecked ? EXCLUDE_COLORS.onFilled : EXCLUDE_COLORS.inkSoft}
        opacity={state.isDisabled ? 0.5 : 1}
        boxShadow={state.isFocused ? 'outline' : undefined}
        transition="background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 120ms ease, transform 90ms ease"
        _active={state.isDisabled ? undefined : { transform: 'scale(0.94)' }}
      >
        {state.isChecked && (
          <Box as="span" aria-hidden data-testid={`${testId}-check`}>
            ✓
          </Box>
        )}
        {label}
      </Box>
    </Box>
  );
};

export const ExcludeModal: React.FC<ExcludeModalProps> = ({
  isOpen,
  onClose,
  loggedAs,
  onSubmit,
  submitState,
  errorMessage,
  reasonOptions,
  finalFocusRef
}) => {
  const [reasons, setReasons] = useState<ExcludeReasonKey[]>([]);
  const [otherCodes, setOtherCodes] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [caseContext, setCaseContext] = useState('');
  /**
   * Null until the first submit attempt, so nothing renders as an error before the user has submitted.
   * `at` drives the live error derivation; `focus` records which group to move focus to for THAT attempt,
   * decided where the validation result is already known. A fresh object per attempt makes it a correct
   * effect dependency.
   */
  const [attempt_, setAttempt] = useState<{
    at: Date;
    focus: 'reason' | 'other' | null;
  } | null>(null);
  const submittedAt = attempt_?.at ?? null;

  const reasonGroupRef = useRef<HTMLElement>(null);
  const otherGroupRef = useRef<HTMLElement>(null);
  const firstPillRef = useRef<HTMLInputElement>(null);
  /** Synchronous activation latch -- see `attempt()` for why the `busy` prop alone is not sufficient. */
  const submittingRef = useRef(false);

  // Defence in depth: the container also remounts this component per open via a key. Both exist because a
  // stale reason or note surviving into a NEW exclusion would be a data-integrity bug, not a cosmetic one.
  useEffect(() => {
    if (isOpen) {
      setReasons([]);
      setOtherCodes([]);
      setNote('');
      setCaseContext('');
      setAttempt(null);
    }
  }, [isOpen]);

  const busy = submitState === 'excluding';
  const vocabulary = reasonOptions ?? [];
  const availableCodes = vocabulary.map((entry) => entry.code);
  const hasOther = reasons.includes('other');

  const values: ExcludeFormValues = { reasons, otherCodes, note, caseContext };

  // Live-derived every render against the CURRENT values but the FIXED `submittedAt`, so editing a field
  // clears its error immediately and nothing flickers from an unrelated re-render.
  const liveResult = submittedAt
    ? buildExcludePayload(values, availableCodes)
    : null;
  const errs: ExcludeValidationError[] =
    liveResult && liveResult.ok === false ? liveResult.errors : [];
  const reasonError = errs.includes('reason-required');
  const otherCodeError = errs.includes('other-code-required');

  // Focus the invalid group AFTER React commits, not synchronously in the click handler. React 18 batches,
  // so a synchronous focus would land while the error node, `aria-invalid` and `aria-describedby` do not yet
  // exist -- and assistive tech computes an element's description at focus time and does not re-read it.
  // Focus the FIRST invalid group after a failed SUBMIT, in render order: reason -> other-codes.
  //
  // The target is decided in `attempt()` (where `result.errors` is already in hand) and carried on the
  // attempt object, rather than read from the live error flags. Two reasons: depending on `reasonError`
  // would re-fire whenever validity flipped back to invalid long after the submit — unchecking the last
  // reason would yank focus with no submit behind it — and mirroring the flags into refs during render, the
  // first fix for that, writes refs during render, which React forbids.
  useEffect(() => {
    if (!attempt_?.focus) return;
    if (attempt_.focus === 'reason') reasonGroupRef.current?.focus();
    else otherGroupRef.current?.focus();
  }, [attempt_]);

  const { getCheckboxProps: getReasonProps } = useCheckboxGroup({
    value: reasons,
    onChange: (next) => {
      const picked = next as ExcludeReasonKey[];
      setReasons(picked);
      // Field coupling: dropping Other must drop its codes in the SAME update, so a stale code can never
      // survive to the payload or reappear if Other is re-selected.
      if (!picked.includes('other')) setOtherCodes([]);
    }
  });
  const { getCheckboxProps: getCodeProps } = useCheckboxGroup({
    value: otherCodes,
    onChange: (next) => setOtherCodes(next as string[])
  });

  const attempt = async (): Promise<void> => {
    // TWO guards, because one is not enough. `busy` is a render-time PROP: the container sets
    // `submitState:'excluding'` inside `onSubmit`, but this component does not see it until that state
    // update commits. Two clicks landing in the same render both read `busy === false` and both call
    // `onSubmit`. The container's own `inFlightRef` then stops the second request reaching the api -- but
    // the UI had already been corrupted, showing "An exclusion is already in progress." and re-enabling the
    // form while the first request was still outstanding. `submittingRef` is synchronous, so the second
    // click cannot get past this line at all.
    if (submittingRef.current || busy) return;
    const result = buildExcludePayload(values, availableCodes);
    setAttempt({
      at: new Date(),
      focus:
        result.ok === false
          ? result.errors.includes('reason-required')
            ? 'reason'
            : 'other'
          : null
    });
    // `result.ok === false`, never `!result.ok`: under this repo's `strict: false` the negation does not
    // narrow the union.
    if (result.ok === false) return; // errors render below; the buttons stay enabled so they can announce
    submittingRef.current = true;
    try {
      await onSubmit(result.payload);
    } finally {
      // Cleared even on a throw, so a rejected submit does not wedge the form permanently closed. The
      // container owns whether a RETRY is allowed (it locks after an unknown outcome); this ref only
      // serializes activation.
      submittingRef.current = false;
    }
  };

  const loggedParts = formatLoggedAtParts(loggedAs.at, loggedAs.timeZone);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="xl"
      finalFocusRef={finalFocusRef}
      initialFocusRef={firstPillRef}
    >
      <ModalOverlay />
      <ModalContent
        data-testid="exclude-modal"
        borderRadius={CASE_ACTION_RADIUS.modal}
        maxW="52rem"
        aria-label={EXCLUDE_MODAL_TITLE}
        aria-busy={busy || undefined}
      >
        <Flex
          align="center"
          gap={3}
          px={7}
          pt={6}
          pb={4}
          borderBottomWidth="1px"
          borderColor={EXCLUDE_COLORS.border}
        >
          <Box
            aria-hidden
            data-testid="exclude-modal-icon"
            bg={EXCLUDE_COLORS.iconBg}
            color={EXCLUDE_COLORS.iconStroke}
            borderRadius={CASE_ACTION_RADIUS.tile}
            boxSize={EXCLUDE_SIZES.headerTile}
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ExcludeGlyph size={EXCLUDE_GLYPH_PX.header} />
          </Box>
          {/* `as="h2"` so heading navigation inside the dialog finds the title; Chakra's default emits a
              paragraph. `textStyle` keeps the visual size unchanged. */}
          <Text as="h2" textStyle="h3">
            {EXCLUDE_MODAL_TITLE}
          </Text>
        </Flex>
        <ModalCloseButton
          aria-label="Close"
          top="2.125rem"
          right={7}
          bg={EXCLUDE_COLORS.inputBg}
          border={`1px solid ${EXCLUDE_COLORS.border}`}
          borderRadius={CASE_ACTION_RADIUS.small}
          size="sm"
          color={EXCLUDE_COLORS.inkMuted}
          _hover={{ bg: EXCLUDE_COLORS.closeHoverBg }}
        />

        {/* THE ONLY `ModalBody`. Chakra wires the dialog's `aria-describedby` to it, so both the subtitle and
            the warning must live here -- the warning is the sole place the product states that exclusion is
            irreversible, and a user who never encounters it before the submit button has not been warned. */}
        <ModalBody px={7} pt={4} pb={0}>
          <Text textStyle="Body 1" color={EXCLUDE_COLORS.inkMuted} mb={4}>
            {EXCLUDE_MODAL_SUBTITLE}
          </Text>
          {/* No `role="alert"` and no `aria-live`: this is present from first paint, and the ARIA Alert
              pattern is for time-sensitive DYNAMIC content. Announcing it as an alert on open would be false
              urgency and would compete with the real validation alert below. It reaches the user through the
              dialog description instead, and again through the submit button's `aria-describedby`. */}
          <Flex
            id={WARNING_ID}
            data-testid="exclude-warning"
            gap={3}
            align="flex-start"
            bg={EXCLUDE_COLORS.warnBg}
            border={`1px solid ${EXCLUDE_COLORS.warnBorder}`}
            borderRadius={CASE_ACTION_RADIUS.control}
            color={EXCLUDE_COLORS.warnInk}
            px={4}
            py={3}
          >
            <Box flexShrink={0} mt="0.125rem">
              <WarningTriangleGlyph
                size={EXCLUDE_GLYPH_PX.warn}
                counterFill={EXCLUDE_COLORS.warnBg}
              />
            </Box>
            <Text textStyle="Body 1">
              <Text as="b">Heavy action.</Text> Once excluded, the case is
              removed from all coder queues and only a{' '}
              <Text as="b">Coding Manager</Text> can restore it.
            </Text>
          </Flex>
        </ModalBody>

        {/* The form renders OUTSIDE ModalBody so it does not become part of the dialog's accessible
            description -- the same trap HoldModal documents. */}
        <Box px={7} pt={5} pb={5} maxH="52vh" overflowY="auto">
          <Box
            as="fieldset"
            ref={reasonGroupRef}
            tabIndex={-1}
            aria-labelledby="exclude-reason-legend"
            aria-invalid={reasonError || undefined}
            aria-describedby={reasonError ? 'exclude-reason-error' : undefined}
            mb={6}
          >
            {/* `display="block"` overrides the UA `legend { display: table }`, which wraps inline content in
                anonymous table cells and swallows the whitespace between the spans. Keeping a real
                `<legend>` (rather than only an `aria-label`) is what associates the VISIBLE eyebrow with the
                group. */}
            <Text
              as="legend"
              id="exclude-reason-legend"
              {...EYEBROW}
              display="block"
              mb={3}
            >
              REASON{' '}
              <Text as="span" color={EXCLUDE_COLORS.error}>
                required
              </Text>{' '}
              <Text as="span" color={EXCLUDE_COLORS.inkFaint} fontWeight={400}>
                select all that apply
              </Text>
            </Text>
            <Flex wrap="wrap" gap={3}>
              {EXCLUDE_REASON_ORDER.map((key, index) => (
                <CheckboxPill
                  key={key}
                  testId={`exclude-reason-${key}`}
                  label={REASON_LABELS[key]}
                  inputRef={index === 0 ? firstPillRef : undefined}
                  // `aria-required` belongs on the INPUT, not on the group: it is not an allowed attribute
                  // on `role=group` (only on `radiogroup`, which a checkbox set must not claim).
                  // Only "Other" reveals further controls, so only it announces the disclosure. `aria-controls`
                  // is set ONLY while expanded -- the target fieldset is conditionally rendered, so pointing
                  // at it while collapsed would be a dangling IDREF.
                  ariaExpanded={key === 'other' ? hasOther : undefined}
                  ariaControls={
                    key === 'other' && hasOther ? 'exclude-other-codes' : undefined
                  }
                  {...getReasonProps({ value: key, isDisabled: busy })}
                />
              ))}
            </Flex>
            {/* Stays MOUNTED once submitted, with its text toggled, so toggling the last reason off and on
                does not remount the live region and re-announce on every click. */}
            {submittedAt && (
              <Text
                data-testid="exclude-reason-error"
                id="exclude-reason-error"
                role="alert"
                color={EXCLUDE_COLORS.error}
                textStyle="Body 1"
                mt={2}
              >
                {reasonError ? 'Select at least one exclusion reason.' : ''}
              </Text>
            )}
          </Box>

          {hasOther && (
            <Box
              as="fieldset"
              id="exclude-other-codes"
              ref={otherGroupRef}
              tabIndex={-1}
              aria-labelledby="exclude-other-legend"
              aria-invalid={otherCodeError || undefined}
              aria-describedby={otherCodeError ? 'exclude-other-error' : undefined}
              mb={6}
            >
              <Text
                as="legend"
                id="exclude-other-legend"
                {...EYEBROW}
                display="block"
                mb={3}
              >
                OTHER REASON{' '}
                <Text
                  as="span"
                  color={EXCLUDE_COLORS.inkFaint}
                  fontWeight={400}
                >
                  select from configured reasons
                </Text>
              </Text>
              {vocabulary.length > 0 ? (
                <Flex wrap="wrap" gap={3}>
                  {vocabulary.map((entry) => (
                    <CheckboxPill
                      key={entry.code}
                      testId={`exclude-other-${entry.code}`}
                      label={entry.label}
                      {...getCodeProps({
                        value: entry.code,
                        isDisabled: busy
                      })}
                    />
                  ))}
                </Flex>
              ) : (
                // Its OWN polite live region. The container's status region is not reachable from here and
                // adding an announcement callback across the boundary for one string would be worse.
                <Text
                  data-testid="exclude-other-empty"
                  role="status"
                  textStyle="Body 1"
                  color={EXCLUDE_COLORS.inkMuted}
                >
                  No configured exclusion reasons are available yet.
                </Text>
              )}
              {otherCodeError && (
                <Text
                  data-testid="exclude-other-error"
                  id="exclude-other-error"
                  role="alert"
                  color={EXCLUDE_COLORS.error}
                  textStyle="Body 1"
                  mt={2}
                >
                  Select at least one configured reason.
                </Text>
              )}
            </Box>
          )}

          <Box mb={6}>
            <Text as="label" htmlFor="exclude-note-input" {...EYEBROW}>
              NOTE{' '}
              <Text as="span" color={EXCLUDE_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="exclude-note-input"
              data-testid="exclude-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              isDisabled={busy}
              placeholder="Add details about this exclusion (optional)…"
              bg={EXCLUDE_COLORS.inputBg}
              borderColor={EXCLUDE_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              px="0.875rem"
              py="0.6875rem"
              _placeholder={{ color: EXCLUDE_COLORS.inkFaint }}
              mt={1}
            />
          </Box>

          <Box>
            <Text as="label" htmlFor="exclude-context-input" {...EYEBROW}>
              ADD CONTEXT FOR THIS CASE{' '}
              <Text as="span" color={EXCLUDE_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="exclude-context-input"
              data-testid="exclude-context"
              value={caseContext}
              onChange={(e) => setCaseContext(e.target.value)}
              isDisabled={busy}
              placeholder="Add context for this case (optional)…"
              bg={EXCLUDE_COLORS.inputBg}
              borderColor={EXCLUDE_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              px="0.875rem"
              py="0.6875rem"
              _placeholder={{ color: EXCLUDE_COLORS.inkFaint }}
              mt={1}
            />
          </Box>
        </Box>

        <ModalFooter
          alignItems="center"
          gap={4}
          px={7}
          sx={{ paddingTop: '1.125rem', paddingBottom: '1.125rem' }}
          borderTopWidth="1px"
          borderColor={EXCLUDE_COLORS.border}
        >
          {/* Three distinct data points, each in its own node, per the ticket's "Logged As" section -- so a
              test can assert the user, the date and the time individually rather than substring-matching one
              opaque string. Placement is the mock's (footer), content is the ticket's. */}
          <Text
            data-testid="exclude-logged-as"
            textStyle="Body 1"
            color={EXCLUDE_COLORS.inkSoft}
            flex="0 1 auto"
          >
            Logged as{' '}
            <Text as="b" data-testid="exclude-logged-as-name" color={EXCLUDE_COLORS.ink}>
              {loggedAs.name}
            </Text>{' '}
            · <Text as="span" data-testid="exclude-logged-as-date">{loggedParts.date}</Text> ·{' '}
            <Text as="span" data-testid="exclude-logged-as-time">{loggedParts.time}</Text>
          </Text>
          {/* The message STACKS ABOVE the buttons rather than sharing their row. It used to be a sibling of
              both buttons inside one wrapping flex, so the moment it appeared the row overflowed and
              "Exclude and Archive" wrapped to a second line, stranding Cancel beside the message. The button
              row is `flexWrap="nowrap"` so it can never reflow again. */}
          <Flex flex="1" direction="column" align="flex-end" gap={2}>
            {submitState === 'error' && errorMessage && errs.length === 0 && (
              <Text
                data-testid="exclude-submit-error"
                role="alert"
                color={EXCLUDE_COLORS.error}
                textStyle="Body 1"
                textAlign="right"
              >
                {errorMessage}
              </Text>
            )}
            <Flex gap={3} align="center" flexWrap="nowrap">
            <AtomButton
              className="btn-secondary"
              data-testid="exclude-cancel"
              onClick={onClose}
            >
              Cancel
            </AtomButton>
            <AtomButton
              className="btn-primary"
              data-testid="exclude-submit"
              onClick={() => void attempt()}
              aria-disabled={busy || undefined}
              // Points at the warning so the risk is announced again at the moment of commitment, not only
              // once on open.
              aria-describedby={WARNING_ID}
              // NOT `isLoading`. Chakra's `isLoading` sets the real `disabled` attribute, which would blur
              // the focused button to `<body>` the instant a submit starts -- the exact focus destruction
              // `aria-disabled` was chosen to avoid. Progress is conveyed by the dialog's `aria-busy` and
              // the container's status region instead; the visual cue is opacity, which costs no semantics.
              style={busy ? { opacity: 0.7, cursor: 'progress' } : undefined}
              bg={EXCLUDE_COLORS.danger}
              color={EXCLUDE_COLORS.onFilled}
              _hover={{ bg: EXCLUDE_COLORS.dangerHover }}
            >
              Exclude and Archive
            </AtomButton>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
